import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import useSound from 'use-sound';
import Clock from './Clock';
import SetMinutesSlider from './SetMinutesSlider';
import ControlButtons from './ControlButtons';
import AlarmModal from './AlarmModal';
import EndSessionDrawer from './EndSessionDrawer';
import SessionStats from './SessionStats';
import BottomNav from '../../components/BottomNav';
import { Time } from './types';
import { useApi } from '../../hooks/useApi';
import { UserProfileResponse } from '../../../../shared/types';
import { isSameDate } from '../../utils/statsUtils';
import { formatCountdown } from '../../utils/clockUtils';
import { shouldIncrementMinute } from '../../utils/timerUtils';

const date = new Date();

const TimerPage = () => {
  const { createSession, getProfile } = useApi();
  const location = useLocation();
  const routeState = location.state as { book?: string; startChapter?: number } | null;

  const [playAlarm, { stop: stopAlarm }] = useSound('/penguin_popstar.mp3', { volume: 0.7 });
  const [playBell] = useSound('/done.mp3', { volume: 0.8 });

  const initialMinutes = 15;
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [worker, setWorker] = useState<Worker | null>(null);
  const [wakeLock, setWakeLock] = useState<any>(null);
  const [time, setTime] = useState<Time>({
    name: 'Bible Reading',
    initialMinutes,
    secondsRemaining: initialMinutes * 60,
    totalSessionTime: 0,
  });

  const [isClockShowing, setIsClockShowing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isAlarmModalOpen, setIsAlarmModalOpen] = useState(false);
  const [isEndSessionOpen, setIsEndSessionOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Restore session from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('bible-timer');
    if (stored) {
      const { totalSessionTime, currentTimeMillis } = JSON.parse(stored);
      if (isSameDate(date, new Date(currentTimeMillis))) {
        setTime((t) => ({ ...t, totalSessionTime }));
      }
    }
  }, []);

  // Load user profile for "Continue Reading" prompt
  useEffect(() => {
    getProfile().then(setProfile).catch(() => {});
  }, []);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Setup web worker
  useEffect(() => {
    const webWorker = new Worker('/worker.js');

    webWorker.addEventListener('message', (e) => {
      const workerSeconds: number = e.data.secondsRemaining;
      setTime((current) => {
        const { name, secondsRemaining, initialMinutes, totalSessionTime } = current;

        if (totalSessionTime !== 0 && !isSubmitting) {
          sessionStorage.setItem('bible-timer', JSON.stringify({
            currentTimeMillis: date.getTime(),
            totalSessionTime: secondsRemaining === 0 ? totalSessionTime + 1 : totalSessionTime,
          }));
        }

        if (secondsRemaining === 1) {
          webWorker.postMessage({ type: 'stop' });
          return { name, initialMinutes, secondsRemaining: 0, totalSessionTime: totalSessionTime + 1 };
        }

        if (shouldIncrementMinute(secondsRemaining, initialMinutes)) {
          return { name, initialMinutes, secondsRemaining: workerSeconds, totalSessionTime: totalSessionTime + 1 };
        }

        return { name, initialMinutes, secondsRemaining: workerSeconds, totalSessionTime };
      });
    });

    setWorker(webWorker);
    return () => webWorker.terminate();
  }, []);

  // Trigger alarm when timer hits 0
  useEffect(() => {
    if (time.secondsRemaining === 0) {
      playAlarm();
      setIsAlarmModalOpen(true);
    }
  }, [time.secondsRemaining]);

  const startFunc = (initSeconds: number) => {
    worker!.postMessage({ type: 'start', secondsRemaining: initSeconds });
    try {
      const nav: any = navigator;
      if ('wakeLock' in navigator) {
        nav.wakeLock.request('screen').then((result: any) => setWakeLock(result));
      }
    } catch {}
    setIsClockShowing(true);
    setIsPaused(false);
  };

  const handleSaveSession = async (minutes: number, book: string, chapters: number[], notes: string) => {
    try {
      await createSession({
        book,
        chapters,
        duration: minutes,
        notes,
      });
      sessionStorage.removeItem('bible-timer');
      posthog.capture('reading_session_completed', {
        book,
        chapters_count: chapters.length,
        chapters,
        duration_minutes: minutes,
        completed_full_duration: minutes >= time.initialMinutes,
        had_notes: notes.trim().length > 0,
      });
      playBell();
    } catch (err) {
      console.error('Save session failed:', err);
      throw err; // re-throw so EndSessionDrawer can catch and show the error
    }
  };

  return (
    <div className="min-h-screen bg-parchment-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm flex flex-col items-center gap-4">
        <h1 className="text-2xl font-bold text-ink">{time.name}</h1>

        {!isClockShowing && !isPaused && (
          <div className="w-full bg-parchment-100 border border-parchment-200 rounded-xl px-4 py-3 shadow-sm">
            <span className="text-sm text-ink">
              📖{' '}
              {profile?.currentBook
                ? `Next up: ${profile.currentBook} ${profile.currentChapter ?? 1}`
                : 'Start in Genesis'}
            </span>
            <p className="text-xs text-ink-muted mt-0.5">Pre-filled when you end your session</p>
          </div>
        )}

        {isClockShowing ? (
          <Clock time={time} />
        ) : isPaused ? (
          <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-8 text-center w-80">
            <p className="text-sm text-ink-muted mb-1">⏸️ Paused</p>
            <p className="text-6xl font-bold text-leather">{time.totalSessionTime}</p>
            <p className="text-sm text-ink-muted mt-1 mb-3">min read</p>
            <div className="border-t border-parchment-200 pt-3 text-sm text-ink-muted">
              Check-in in {formatCountdown(time.secondsRemaining)}
            </div>
          </div>
        ) : (
          <SetMinutesSlider time={time} setTime={setTime} />
        )}

        <ControlButtons
          startFunc={startFunc}
          time={time}
          wakeLock={wakeLock}
          setWakeLock={setWakeLock}
          setTime={setTime}
          setIsClockShowing={setIsClockShowing}
          isPaused={isPaused}
          setIsPaused={setIsPaused}
          isClockShowing={isClockShowing}
          setIsEndSessionScreenOpen={setIsEndSessionOpen}
          worker={worker}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
        />

        <SessionStats time={time} minutesSpent={0} />
      </div>

      <AlarmModal
        isOpen={isAlarmModalOpen}
        onClose={() => { stopAlarm(); setIsAlarmModalOpen(false); setIsClockShowing(false); }}
        onStopButtonClick={() => {
          stopAlarm();
          setIsAlarmModalOpen(false);
          setIsClockShowing(false);
          setIsEndSessionOpen(true);
        }}
        handleRepeat={() => {
          stopAlarm();
          setIsAlarmModalOpen(false);
          startFunc(time.initialMinutes * 60);
        }}
      />

      <EndSessionDrawer
        time={time}
        isOpen={isEndSessionOpen}
        onClose={() => setIsEndSessionOpen(false)}
        onSave={handleSaveSession}
        defaultBook={routeState?.book ?? profile?.currentBook ?? 'Genesis'}
        defaultChapter={routeState?.startChapter ?? profile?.currentChapter ?? 1}
        currentReadThrough={profile?.currentReadThrough ?? 1}
      />

      <BottomNav hidden={isClockShowing} />
    </div>
  );
};

export default TimerPage;
